import { createClient } from '@/lib/supabase/client';

export interface FAQ {
  id: string;
  question: string;
  answer: string;
  category: string;
  displayOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface FAQInput {
  question: string;
  answer: string;
  category: string;
  displayOrder: number;
  isActive?: boolean;
}

export interface FAQsByCategory {
  category: string;
  faqs: FAQ[];
}

class FAQService {
  async getAll(): Promise<FAQ[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('faqs')
      .select('*')
      .order('display_order', { ascending: true });

    if (error) throw new Error(error.message);
    
    return (data || []).map(this.mapFromDb);
  }

  async getActive(): Promise<FAQ[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('faqs')
      .select('*')
      .eq('is_active', true)
      .order('display_order', { ascending: true });

    if (error) throw new Error(error.message);
    
    return (data || []).map(this.mapFromDb);
  }

  async getByCategory(category: string): Promise<FAQ[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('faqs')
      .select('*')
      .eq('category', category)
      .eq('is_active', true)
      .order('display_order', { ascending: true });

    if (error) throw new Error(error.message);
    
    return (data || []).map(this.mapFromDb);
  }

  async getActiveGroupedByCategory(): Promise<FAQsByCategory[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('faqs')
      .select('*')
      .eq('is_active', true)
      .order('category', { ascending: true })
      .order('display_order', { ascending: true });

    if (error) throw new Error(error.message);
    
    const faqs = (data || []).map(this.mapFromDb);
    
    // Group FAQs by category
    const grouped = faqs.reduce((acc, faq) => {
      const existing = acc.find(g => g.category === faq.category);
      if (existing) {
        existing.faqs.push(faq);
      } else {
        acc.push({ category: faq.category, faqs: [faq] });
      }
      return acc;
    }, [] as FAQsByCategory[]);
    
    return grouped;
  }

  async getAllCategories(): Promise<string[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('faqs')
      .select('category')
      .order('category', { ascending: true });

    if (error) throw new Error(error.message);
    
    // Get unique categories
    const categories = [...new Set((data || []).map(item => item.category))];
    return categories;
  }

  async getById(id: string): Promise<FAQ | null> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('faqs')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw new Error(error.message);
    
    return data ? this.mapFromDb(data) : null;
  }

  async create(input: FAQInput): Promise<FAQ | null> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('faqs')
      .insert([{
        question: input.question,
        answer: input.answer,
        category: input.category,
        display_order: input.displayOrder,
        is_active: input.isActive ?? true,
        created_by: user.id,
      }])
      .select()
      .single();

    if (error) throw new Error(error.message);
    
    return data ? this.mapFromDb(data) : null;
  }

  async update(id: string, input: Partial<FAQInput>): Promise<FAQ | null> {
    const supabase = createClient();
    const updateData: any = {};
    
    if (input.question !== undefined) updateData.question = input.question;
    if (input.answer !== undefined) updateData.answer = input.answer;
    if (input.category !== undefined) updateData.category = input.category;
    if (input.displayOrder !== undefined) updateData.display_order = input.displayOrder;
    if (input.isActive !== undefined) updateData.is_active = input.isActive;

    const { data, error } = await supabase
      .from('faqs')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) throw new Error(error.message);
    
    return data ? this.mapFromDb(data) : null;
  }

  async delete(id: string): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase
      .from('faqs')
      .delete()
      .eq('id', id);

    if (error) throw new Error(error.message);
  }

  private mapFromDb(data: any): FAQ {
    return {
      id: data.id,
      question: data.question,
      answer: data.answer,
      category: data.category,
      displayOrder: data.display_order,
      isActive: data.is_active,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    };
  }
}

export const faqService = new FAQService();